import cx from 'classnames'
import styles from './ButtonSettingsForm.module.css'
import Heading from '../../../../components/Heading'
import Input from '../../../../components/Input'
import Select from '../../../../components/Select'
import CircleColorPicker from '../../../../components/CircleColorPicker'
import IndividualizationButton from '../IndividualizationButton/IndividualizationButton'
import Button from '../../../../components/Button'
import React, { useEffect, useMemo } from 'react'
import { ButtonSetting } from '../../../../types/ButtonSetting'
import { useTranslation } from 'react-i18next'
import { useFetchSingle } from '../../../../hooks/useFetchSingle'
import { ApiRoutes } from '../../../../services/ApiService'
import * as yup from 'yup'
import { useStoreData } from '../../../../hooks/useStoreData'
import { showToast } from '../../../../services/ToastService'
import Loader from '../../../../components/Loader'

const ButtonSettingsForm: React.FC = () => {
    const { t } = useTranslation(['settings'])
    const { data, isLoading, fetch } = useFetchSingle<ButtonSetting>(
        ApiRoutes.BUTTON_SETTINGS
    )

    const options = useMemo(() => {
        return [
            { value: 'html', label: t('button.options.html') },
            { value: 'after', label: t('button.options.after') },
            { value: 'append', label: t('button.options.append') },
            { value: 'before', label: t('button.options.before') },
            { value: 'prepend', label: t('button.options.prepend') },
        ]
    }, [t])

    const updateButtonSettingsSchema = useMemo(() => {
        return {
            selector: yup.string().required(),
            position: yup
                .string()
                .required()
                .oneOf(options.map((option) => option.value)),
            text: yup.string().required().min(3),
            classes: yup.string(),
            text_color: yup.string().required(),
            background_color: yup.string().required(),
        }
    }, [options])

    const {
        element: buttonSettings,
        isValid,
        submit,
        changeProp: onChange,
    } = useStoreData<ButtonSetting>(
        ApiRoutes.BUTTON_SETTINGS,
        updateButtonSettingsSchema
    )

    useEffect(() => {
        fetch()
    }, [fetch])

    useEffect(() => {
        if (data) onChange(data)
    }, [data, onChange])

    const handleSubmit = () => {
        submit().then((res) => {
            if (res.data?.message) {
                showToast(t('button.success'))
            } else {
                showToast(t('button.error'), 'error')
            }
        })
    }

    return !isLoading ? (
        <div>
            <div className={cx(styles['button-list'])}>
                <div className={'zr-col'}>
                    <Heading level={4} classes={'zr-mt-4 zr-mb-2'}>
                        {t('button.preview')}
                    </Heading>
                    {buttonSettings && data && (
                        <IndividualizationButton
                            text={buttonSettings.text ?? data.text}
                            background_color={
                                buttonSettings.background_color ??
                                data.background_color
                            }
                            text_color={
                                buttonSettings.text_color ?? data.text_color
                            }
                            icon_color={
                                buttonSettings.icon_color ?? data.icon_color
                            }
                            classes={buttonSettings.classes ?? data.classes}
                        />
                    )}
                </div>
                <div className={'zr-col'}>
                    <Heading level={4} classes={'zr-mt-4 zr-mb-2'}>
                        {t('button.sub_heading')}
                    </Heading>
                    <div className={cx(styles['two-input-row'])}>
                        <Input
                            type={'text'}
                            placeholder={t('button.labels.selector')}
                            property={'selector'}
                            value={buttonSettings.selector ?? ''}
                            onChange={(_, value) =>
                                onChange({ selector: value })
                            }
                        />
                        <Select
                            placeholder={t('button.labels.position')}
                            searchable={false}
                            options={options}
                            property={'position'}
                            value={buttonSettings?.position ?? ''}
                            onSelect={(_, value) =>
                                onChange({ position: value })
                            }
                        />
                    </div>
                    <Heading level={4} classes={'zr-mt-4 zr-mb-2'}>
                        Design
                    </Heading>
                    <div className={cx(styles['two-input-row'])}>
                        <Input
                            type={'text'}
                            placeholder={t('button.labels.text')}
                            property={'text'}
                            value={buttonSettings?.text ?? ''}
                            onChange={(_, value) => onChange({ text: value })}
                            multiLanguage={true}
                            multiLanguageValue={buttonSettings.text_multi}
                            onMultiLanguageChange={(_, value) =>
                                onChange({ text_multi: value })
                            }
                        />
                        <Input
                            type={'text'}
                            placeholder={t('button.labels.css')}
                            property={'classes'}
                            value={buttonSettings?.classes ?? ''}
                            onChange={(_, value) =>
                                onChange({ classes: value })
                            }
                        />
                    </div>
                    <div className={'zr-row zr-gap-4'}>
                        <CircleColorPicker
                            property={'text_color'}
                            value={buttonSettings?.text_color ?? ''}
                            onChange={(_, value) =>
                                onChange({ text_color: value })
                            }
                            label={t('button.labels.text_color')}
                        />
                        <CircleColorPicker
                            property={'text_color'}
                            value={buttonSettings?.icon_color ?? ''}
                            onChange={(_, value) =>
                                onChange({ icon_color: value })
                            }
                            label={t('button.labels.icon_color')}
                        />
                        <CircleColorPicker
                            property={'background_color'}
                            value={buttonSettings?.background_color ?? ''}
                            onChange={(_, value) =>
                                onChange({ background_color: value })
                            }
                            label={t('button.labels.background_color')}
                        />
                    </div>
                </div>
                <Button
                    hierarchy={'primary'}
                    classes={cx(styles['form-button'])}
                    text={t('button.action')}
                    fn={() => handleSubmit()}
                    disabled={!isValid}
                />
            </div>
        </div>
    ) : (
        <div className={'zr-d-flex zr-w-100 zr-justify-center'}>
            <Loader isLoading={isLoading} size={'m'} />
        </div>
    )
}

export default ButtonSettingsForm
